import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    InputBase,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { toChecksumId } from '../../utils/ids';
import { FLOW_GROUPS, type FlowGroupId } from '../flow/groups';
import {
    useMarketWallets,
    WALLET_PAGE_SIZE,
    type MarketWallet,
    type WalletSort,
    type WalletTab,
} from './useMarketWallets';
import type { TradeTypeId, WalletTrade } from './tradeTypes';
import { WALLET_GRID, WalletRow } from './WalletRow';

interface WalletsPanelProps {
    marketId: string;
    selectedWallets: readonly string[];
    colors: ReadonlyMap<string, string>;
    tradesByWallet: ReadonlyMap<string, readonly WalletTrade[]>;
    loadingWallets: ReadonlySet<string>;
    failedWallets: ReadonlySet<string>;
    onRetryTrades: (address: string) => void;
    tradeTypes: readonly TradeTypeId[];
    onTradeTypesChange: (types: TradeTypeId[]) => void;
    onToggleWallet: (address: string) => void;
    onClearWallets: () => void;
}

export function defaultTab(counts: Record<string, number> | null): WalletTab {
    return (counts?.[FLOW_GROUPS[0].id] ?? 0) > 0 ? FLOW_GROUPS[0].id : 'all';
}

function labelOf(group: FlowGroupId): string {
    return FLOW_GROUPS.find(g => g.id === group)?.label.toLowerCase() ?? group;
}

function HeadCell({
    children,
    sortKey,
    active,
    onSort,
    align = 'right',
}: {
    children?: React.ReactNode;
    sortKey?: WalletSort;
    active?: boolean;
    onSort?: (key: WalletSort) => void;
    align?: 'left' | 'right';
}) {
    return (
        <Typography
            onClick={sortKey && onSort ? () => onSort(sortKey) : undefined}
            sx={{
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                textAlign: align,
                color: active ? 'text.primary' : 'text.disabled',
                cursor: sortKey ? 'pointer' : 'default',
                userSelect: 'none',
            }}
        >
            {children}
            {active ? ' ↓' : ''}
        </Typography>
    );
}

function noop() {}

const FOOTER_SX = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    pt: 0.75,
} as const;

// A wallet as the list usually shows one. Drawn invisible under each
// placeholder row, so a row is as tall as a real one on any screen (on a touch
// screen a row's controls are larger). Its row is in the page, hidden.
const STAND_IN: MarketWallet = {
    address: '0x0000000000000000000000000000000000000000',
    alias: null,
    tags: [],
    side: 'YES',
    net_shares: 1_000_000,
    net_exposure_usd: 500_000,
    realized_pnl: 100_000,
    unrealized_pnl: 50_000,
    total_pnl: 150_000,
    trade_count: 40,
    group_memberships: [],
    scores: {},
};
// A placeholder as wide as the row; the text variant draws it a little lower
// than the row, which leaves a gap between two of them.
const ROW_PLACEHOLDER_SX = {
    display: 'block',
    maxWidth: 'none',
    width: '100%',
} as const;

// A full page of rows and the line under them, as tall as the list will be,
// so the page below does not move when it lands.
function WalletsPlaceholder({
    tradeTypes,
}: {
    tradeTypes: readonly TradeTypeId[];
}) {
    return (
        <Box data-testid="wallets-placeholder" aria-hidden>
            {Array.from({ length: WALLET_PAGE_SIZE }, (_, i) => (
                <Skeleton key={i} sx={ROW_PLACEHOLDER_SX}>
                    <WalletRow
                        address={STAND_IN.address}
                        wallet={STAND_IN}
                        maxExposure={STAND_IN.net_exposure_usd}
                        color={null}
                        selected={false}
                        trades={undefined}
                        tradesLoading={false}
                        tradesFailed={false}
                        onRetryTrades={noop}
                        tradeTypes={tradeTypes}
                        onTradeTypesChange={noop}
                        onToggle={noop}
                    />
                </Skeleton>
            ))}
            <Skeleton sx={{ ...ROW_PLACEHOLDER_SX, width: 'fit-content' }}>
                <Box sx={FOOTER_SX}>
                    <Typography sx={{ fontSize: '11px' }}>
                        10 of 37 wallets
                    </Typography>
                    <Button
                        size="small"
                        sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
                    >
                        Show more
                    </Button>
                </Box>
            </Skeleton>
        </Box>
    );
}

export function WalletsPanel({
    marketId,
    selectedWallets,
    colors,
    tradesByWallet,
    loadingWallets,
    failedWallets,
    onRetryTrades,
    tradeTypes,
    onTradeTypesChange,
    onToggleWallet,
    onClearWallets,
}: WalletsPanelProps) {
    const [chosenTab, setChosenTab] = useState<WalletTab | null>(null);
    const [sort, setSort] = useState<WalletSort>('exposure');
    const [address, setAddress] = useState('');

    useEffect(() => {
        setChosenTab(null);
    }, [marketId]);

    const tab: WalletTab = chosenTab ?? 'all';
    const {
        wallets,
        total,
        hasMore,
        groupCounts,
        loading,
        loadingMore,
        error,
        loadedTab,
        reload,
        loadMore,
    } = useMarketWallets(marketId, tab, sort);

    const pendingDefault =
        chosenTab === null && groupCounts !== null
            ? defaultTab(groupCounts)
            : null;
    useEffect(() => {
        if (pendingDefault !== null) setChosenTab(pendingDefault);
    }, [pendingDefault]);

    const settled = !loading && (error || loadedTab === tab);
    const waiting = !settled || (pendingDefault !== null && !error);

    const listed = new Set(wallets.map(w => toChecksumId(w.address)));
    const extra = selectedWallets.filter(w => !listed.has(w));
    const selected = new Set(selectedWallets);
    const groupTabs = FLOW_GROUPS.filter(
        g => (groupCounts?.[g.id] ?? 0) > 0 || tab === g.id
    );
    const maxExposure = wallets.reduce(
        (max, w) => Math.max(max, Math.abs(w.net_exposure_usd)),
        0
    );

    const submitAddress = () => {
        const value = address.trim();
        if (!value) return;
        const id = toChecksumId(value);
        if (!selected.has(id)) onToggleWallet(id);
        setAddress('');
    };

    return (
        <Box data-testid="wallets-panel">
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 0.5,
                }}
            >
                <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                    Wallets
                </Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={tab}
                    onChange={(_, value: WalletTab | null) => {
                        if (value) setChosenTab(value);
                    }}
                    sx={{
                        height: 24,
                        '& .MuiToggleButton-root': {
                            py: 0,
                            px: 1,
                            fontSize: '11px',
                            textTransform: 'none',
                        },
                    }}
                >
                    {groupTabs.map(group => (
                        <ToggleButton key={group.id} value={group.id}>
                            {group.label}
                            {groupCounts
                                ? ` (${groupCounts[group.id] ?? 0})`
                                : ''}
                        </ToggleButton>
                    ))}
                    <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
                {selectedWallets.length > 0 && (
                    <Button
                        size="small"
                        onClick={onClearWallets}
                        sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
                    >
                        Clear {selectedWallets.length} selected
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <InputBase
                    value={address}
                    onChange={event => setAddress(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === 'Enter') submitAddress();
                    }}
                    placeholder="Add wallet by address"
                    inputProps={{ 'aria-label': 'Add wallet by address' }}
                    sx={{
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        width: 260,
                        px: 1,
                        height: 26,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                    }}
                />
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: WALLET_GRID,
                    columnGap: 2,
                    px: 1.5,
                    py: 0.5,
                }}
            >
                <HeadCell align="left">Wallet</HeadCell>
                <HeadCell align="left">Group</HeadCell>
                <HeadCell align="left">Side</HeadCell>
                <HeadCell
                    sortKey="exposure"
                    active={sort === 'exposure'}
                    onSort={setSort}
                >
                    Net exposure
                </HeadCell>
                <HeadCell
                    sortKey="pnl"
                    active={sort === 'pnl'}
                    onSort={setSort}
                >
                    PnL
                </HeadCell>
                <HeadCell>Trades</HeadCell>
                <HeadCell align="left">Tags</HeadCell>
            </Box>

            {extra.map(wallet => (
                <WalletRow
                    key={wallet}
                    address={wallet}
                    wallet={null}
                    maxExposure={maxExposure}
                    color={colors.get(wallet) ?? null}
                    selected
                    trades={tradesByWallet.get(wallet)}
                    tradesLoading={loadingWallets.has(wallet)}
                    tradesFailed={failedWallets.has(wallet)}
                    onRetryTrades={() => onRetryTrades(wallet)}
                    tradeTypes={tradeTypes}
                    onTradeTypesChange={onTradeTypesChange}
                    onToggle={() => onToggleWallet(wallet)}
                />
            ))}

            {waiting && <WalletsPlaceholder tradeTypes={tradeTypes} />}

            {!waiting && error && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.5,
                    }}
                >
                    <Typography sx={{ fontSize: '12px', color: 'error.main' }}>
                        The wallet list could not be loaded.
                    </Typography>
                    <Button
                        size="small"
                        onClick={reload}
                        sx={{ fontSize: '11px', textTransform: 'none', py: 0 }}
                    >
                        Try again
                    </Button>
                </Box>
            )}

            {!waiting && !error && wallets.length === 0 && (
                <Typography
                    sx={{
                        fontSize: '12px',
                        color: 'text.disabled',
                        px: 1.5,
                        py: 1,
                    }}
                >
                    {tab === 'all'
                        ? 'No wallets hold a position in this outcome.'
                        : `No ${labelOf(tab)} wallets in this outcome.`}
                </Typography>
            )}

            {!waiting &&
                !error &&
                wallets.map(wallet => {
                    const id = toChecksumId(wallet.address);
                    return (
                        <WalletRow
                            key={wallet.address}
                            address={id}
                            wallet={wallet}
                            maxExposure={maxExposure}
                            color={colors.get(id) ?? null}
                            selected={selected.has(id)}
                            trades={tradesByWallet.get(id)}
                            tradesLoading={loadingWallets.has(id)}
                            tradesFailed={failedWallets.has(id)}
                            onRetryTrades={() => onRetryTrades(id)}
                            tradeTypes={tradeTypes}
                            onTradeTypesChange={onTradeTypesChange}
                            onToggle={() => onToggleWallet(id)}
                        />
                    );
                })}

            {!waiting && !error && wallets.length > 0 && (
                <Box sx={FOOTER_SX}>
                    <Typography
                        sx={{ fontSize: '11px', color: 'text.secondary' }}
                    >
                        {total !== null
                            ? `${wallets.length} of ${total} wallets`
                            : `${wallets.length} wallets`}
                    </Typography>
                    {hasMore && (
                        <Button
                            size="small"
                            disabled={loadingMore}
                            onClick={loadMore}
                            startIcon={
                                loadingMore ? (
                                    <CircularProgress size={10} />
                                ) : undefined
                            }
                            sx={{
                                fontSize: '11px',
                                textTransform: 'none',
                                py: 0,
                            }}
                        >
                            Show more
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
}
